// apps/dashboard/src/lib/entry-referrer.ts

/**
 * Where did this visit come from, as an ORIGIN only (never a path or query, which can
 * carry search terms or tokens).
 *
 * `document.referrer` is fixed at page load and goes stale on client-side (SPA)
 * navigations, so it only describes the visit's entry page. The pathname at module load
 * identifies that entry page: while we are still on it, the referrer is real; once the
 * visitor has navigated within the app, we report our own origin so it reads as internal.
 *
 * Duplicated per B2C app (same convention as anon-id.ts) — keep the copies in sync.
 */

// Captured once when the bundle first evaluates, i.e. on the entry page load. Undefined
// during SSR/build, where there is no location to capture.
const initialPathname: string | undefined =
  typeof window !== 'undefined' ? window.location.pathname : undefined;

/** Reduces a URL string to its http(s) origin, or null when invalid or non-web. */
export function originOf(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    return parsed.origin;
  } catch {
    return null;
  }
}

export function getEntryReferrerOrigin(): string | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  if (window.location.pathname !== initialPathname) return window.location.origin;
  return document.referrer ? originOf(document.referrer) : null;
}
